<template>
  <div class="wn-new">
    <!-- SWITCH TO OLD VERSION -->
    <div
      class="wn-switch-banner"
      @click="goToOldVersion()">
      <v-icon
        small
        color="#c1167c"
        class="mr-2">
        mdi-rocket-launch-outline
      </v-icon>
      <span class="wn-switch-banner__text">กลับไปหน้า Withdraw Note เวอร์ชันเดิม</span>
      <span class="wn-switch-banner__cta">
        Switch to old version
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.2"
          stroke-linecap="round"
          stroke-linejoin="round">
          <line
            x1="5"
            y1="12"
            x2="19"
            y2="12" />
          <polyline points="12 5 19 12 12 19" />
        </svg>
      </span>
    </div>

    <!-- PAGE HEADER -->
    <div class="wn-page-header">
      <div>
        <h1 class="wn-title">
          Withdraw Notes
        </h1>
        <p class="wn-subtitle">
          Manage outbound withdraw notes across warehouses
        </p>
      </div>
      <div class="wn-header-actions">
        <button
          class="wn-btn wn-btn--primary"
          :disabled="isDisabled || isDisabledByRole"
          @click="createNote()">
          <svg
            width="17"
            height="17"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.2"
            stroke-linecap="round">
            <line
              x1="12"
              y1="5"
              x2="12"
              y2="19" />
            <line
              x1="5"
              y1="12"
              x2="19"
              y2="12" />
          </svg>
          Create Note
        </button>
      </div>
    </div>

    <!-- CARD -->
    <div class="wn-card">
      <!-- TOOLBAR -->
      <div class="wn-toolbar">
        <div class="wn-field">
          <label class="wn-field__label">Warehouse</label>
          <v-autocomplete
            :value="warehouseSelected"
            class="wn-autocomplete"
            :items="warehouses"
            item-text="name"
            item-value="id"
            :menu-props="{ offsetY: true }"
            placeholder="Select warehouse"
            hide-details
            dense
            solo
            flat
            append-icon="mdi-chevron-down"
            @change="setWarehouse($event)" />
        </div>

        <div class="wn-spacer"></div>

        <div class="wn-search">
          <svg
            class="wn-search__icon"
            width="17"
            height="17"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#9aa5b1"
            stroke-width="2">
            <circle
              cx="11"
              cy="11"
              r="7" />
            <line
              x1="21"
              y1="21"
              x2="16.65"
              y2="16.65" />
          </svg>
          <input
            v-model="searchInput"
            class="wn-search__input"
            placeholder="Search code or note…"
            @input="onSearchInput()"
            @keydown.enter="onSearch()" />
        </div>
      </div>

      <!-- TABS + EXPORT/PRINT -->
      <div class="wn-tabs-row">
        <div class="wn-tabs">
          <button
            v-for="(tab, index) in tabsTitle"
            :key="tab.key"
            class="wn-tab"
            :class="{ 'wn-tab--active': tabs === index }"
            @click="changeTab(index)">
            {{ tab.label }}
            <span class="wn-tab__badge">
              <v-progress-circular
                v-if="loadingCount"
                indeterminate
                size="14"
                width="2" />
              <template v-else>{{ formatNumber(tab.count) }}</template>
            </span>
          </button>
        </div>
        <div class="wn-tabs-actions">
          <button
            class="wn-action-btn"
            :disabled="!hasSelection || exporting"
            @click="exportItems()">
            <v-progress-circular
              v-if="exporting"
              indeterminate
              size="16"
              width="2" />
            <svg
              v-else
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line
                x1="12"
                y1="15"
                x2="12"
                y2="3" />
            </svg>
            Export Item
          </button>
          <button
            class="wn-action-btn wn-action-btn--primary"
            :disabled="!canPrint"
            @click="printSelected()">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2">
              <polyline points="6 9 6 2 18 2 18 9" />
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
              <rect
                x="6"
                y="14"
                width="12"
                height="8" />
            </svg>
            Print ({{ selectedCount }})
          </button>
        </div>
      </div>

      <!-- SELECTION BANNER -->
      <div
        v-if="hasSelection"
        class="wn-sel-banner">
        <span class="wn-sel-banner__text">{{ selectedCount }} selected</span>
        <div class="wn-spacer"></div>
        <button
          class="wn-sel-btn"
          @click="clearSelection()">
          Clear
        </button>
        <button
          class="wn-sel-btn wn-sel-btn--solid"
          @click="printSelected()">
          Print selected
        </button>
      </div>

      <!-- TOP PAGINATION -->
      <div class="wn-pagination wn-pagination--top">
        <div class="wn-rows-per-page">
          <span class="wn-rows-per-page__label">Rows per page:</span>
          <v-menu
            offset-y
            :close-on-content-click="true">
            <template #activator="{ on, attrs }">
              <button
                class="wn-rows-per-page__btn"
                v-bind="attrs"
                v-on="on">
                {{ perPage }}
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#7b8794"
                  stroke-width="2.2"
                  stroke-linecap="round">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
            </template>
            <v-list dense>
              <v-list-item
                v-for="size in perPageOptions"
                :key="size"
                @click="setPerPage(size)">
                <v-list-item-title>{{ size }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
        <span class="wn-range">{{ rangeStart }}–{{ rangeEnd }} of {{ formatNumber(activeCount) }}</span>
        <div class="wn-pager">
          <button
            title="Previous"
            class="wn-pager__btn"
            :disabled="page <= 1 || getting"
            @click="prevPage()">
            <svg
              width="17"
              height="17"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.2"
              stroke-linecap="round"
              stroke-linejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <button
            title="Next"
            class="wn-pager__btn"
            :disabled="page >= totalPages || getting"
            @click="nextPage()">
            <svg
              width="17"
              height="17"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.2"
              stroke-linecap="round"
              stroke-linejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>
      </div>

      <!-- TABLE -->
      <div class="wn-table-scroll">
        <div class="wn-table">
          <!-- HEADER ROW -->
          <div class="wn-row wn-row--head">
            <div class="wn-cell-check">
              <div
                v-if="canSelect"
                class="wn-check"
                :class="{ 'wn-check--on': allSelected }"
                @click="toggleAll()">
                <svg
                  v-if="allSelected"
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#fff"
                  stroke-width="3.2"
                  stroke-linecap="round"
                  stroke-linejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
            </div>
            <div class="wn-th">
              Document No.
            </div>
            <div class="wn-th wn-th--center">
              Create Date
            </div>
            <div class="wn-th wn-th--center">
              Due Date
            </div>
            <div class="wn-th">
              Note
            </div>
            <div class="wn-th wn-th--right">
              Actions
            </div>
          </div>

          <!-- DATA ROWS -->
          <div
            v-for="item in rows"
            :key="item.id"
            class="wn-row"
            :class="{ 'wn-row--selected': isSelected(item) }">
            <div class="wn-cell-check">
              <div
                v-if="canSelect"
                class="wn-check"
                :class="{ 'wn-check--on': isSelected(item) }"
                @click="toggleSelect(item)">
                <svg
                  v-if="isSelected(item)"
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#fff"
                  stroke-width="3.2"
                  stroke-linecap="round"
                  stroke-linejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
            </div>

            <div>
              <div class="wn-doc">
                <span
                  class="wn-doc__type"
                  :style="typeStyle(item)">{{ docType(item) }}</span>
                <a
                  class="wn-doc__code"
                  href="#"
                  @click.prevent="openDetail(item)">{{ docNumber(item) }}</a>
                <button
                  class="wn-doc__copy"
                  :title="copiedId === item.id ? 'Copied!' : 'Copy document no.'"
                  @click="copyCode(item)">
                  <svg
                    v-if="copiedId === item.id"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#1f8a50"
                    stroke-width="2.4"
                    stroke-linecap="round"
                    stroke-linejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <svg
                    v-else
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round">
                    <rect
                      x="9"
                      y="9"
                      width="13"
                      height="13"
                      rx="2"
                      ry="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                </button>
              </div>
              <div class="wn-loc">
                <span
                  class="wn-loc__chip"
                  :style="chipStyle('origin')">
                  {{ currentWarehouseName(item) }}
                </span>
                <template v-if="destChip(item)">
                  <svg
                    class="wn-loc__arrow"
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#b6c0c9"
                    stroke-width="2.4"
                    stroke-linecap="round"
                    stroke-linejoin="round">
                    <line
                      x1="5"
                      y1="12"
                      x2="19"
                      y2="12" />
                    <polyline points="13 6 19 12 13 18" />
                  </svg>
                  <span
                    class="wn-loc__chip"
                    :style="chipStyle('dest')">
                    {{ destChip(item).name }}
                  </span>
                </template>
              </div>
            </div>

            <div class="wn-cell--center">
              <div class="wn-date">
                {{ formatDate(item.createdAt) }}
              </div>
              <div class="wn-time">
                {{ formatTime(item.createdAt) }}
              </div>
            </div>

            <div class="wn-cell--center">
              <div
                v-if="noteStatus === 'pending' && item.dueDate"
                class="wn-date wn-due"
                :class="dueDateClass(item.dueDate)">
                {{ formatDate(item.dueDate) }}
              </div>
              <div
                v-else
                class="wn-date wn-date--empty">
                —
              </div>
            </div>

            <div
              class="wn-note"
              :title="noteCompute(item)">
              {{ noteCompute(item) }}
            </div>

            <div class="wn-actions">
              <button
                title="Print"
                class="wn-icon-btn"
                :class="{ 'wn-icon-btn--printed': item.print }"
                @click="printOne(item)">
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2">
                  <polyline points="6 9 6 2 18 2 18 9" />
                  <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                  <rect
                    x="6"
                    y="14"
                    width="12"
                    height="8" />
                </svg>
              </button>
            </div>
          </div>

          <!-- LOADING -->
          <div
            v-if="getting"
            class="wn-empty">
            <v-progress-circular
              indeterminate
              color="#0f5d7c"
              size="28" />
          </div>

          <!-- EMPTY -->
          <div
            v-else-if="isEmpty"
            class="wn-empty">
            <div class="wn-empty__title">
              No withdraw notes found
            </div>
            <div class="wn-empty__sub">
              Try a different search or tab.
            </div>
          </div>
        </div>
      </div>

      <!-- FOOTER PAGINATION -->
      <div class="wn-pagination wn-pagination--footer">
        <div class="wn-rows-per-page">
          <span class="wn-rows-per-page__label">Rows per page:</span>
          <v-menu
            offset-y
            top
            :close-on-content-click="true">
            <template #activator="{ on, attrs }">
              <button
                class="wn-rows-per-page__btn"
                v-bind="attrs"
                v-on="on">
                {{ perPage }}
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#7b8794"
                  stroke-width="2.2"
                  stroke-linecap="round">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
            </template>
            <v-list dense>
              <v-list-item
                v-for="size in perPageOptions"
                :key="size"
                @click="setPerPage(size)">
                <v-list-item-title>{{ size }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
        <span class="wn-range">{{ rangeStart }}–{{ rangeEnd }} of {{ formatNumber(activeCount) }}</span>
        <div class="wn-pager">
          <button
            title="Previous"
            class="wn-pager__btn"
            :disabled="page <= 1 || getting"
            @click="prevPage()">
            <svg
              width="17"
              height="17"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.2"
              stroke-linecap="round"
              stroke-linejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <button
            title="Next"
            class="wn-pager__btn"
            :disabled="page >= totalPages || getting"
            @click="nextPage()">
            <svg
              width="17"
              height="17"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.2"
              stroke-linecap="round"
              stroke-linejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { getRole } from '@/assets/js/Authentication'
import InventoryProvider from '@/resources/InventoryProvider'

const InventoryService = new InventoryProvider()

const TYPE_COLORS = {
  ALO: { fg: '#2d52b8' },
  TFO: { fg: '#c2701a' },
  GRO: { fg: '#1f8a50' }
}

const CHIP_COLORS = {
  origin: { bg: '#e1f0f7', fg: '#0d6a8f' },
  dest: { bg: '#f1e9f4', fg: '#824a8f' }
}

export default {
  name: 'WithdrawNoteNew',
  data () {
    return {
      tabsTitle: [
        { key: 'pending', label: 'Pending', count: 0 },
        { key: 'approved', label: 'Approved', count: 0 },
        { key: 'canceled', label: 'Cancelled', count: 0 }
      ],
      tabs: 0,
      searchInput: '',
      searchTimeout: null,
      typeFilter: 'all',
      page: 1,
      perPage: 25,
      perPageOptions: [25, 50, 100],
      totalPages: 1,
      getting: false,
      loadingCount: false,
      withdrawNotesData: [],
      selected: {},
      warehouseSelected: null,
      copiedId: null,
      copiedTimeout: null,
      exporting: false
    }
  },
  computed: {
    ...mapGetters({
      warehouse: 'Store/warehouse',
      mapWarehouse: 'Store/mapWarehouse'
    }),
    warehouses () {
      return this.mapWarehouse.filter((v) => v.id !== 0)
    },
    warehouseName () {
      const found = this.warehouses.find((w) => w.id === this.warehouseSelected)
      return found ? found.name : 'Select warehouse'
    },
    noteStatus () {
      return this.tabsTitle[this.tabs].key
    },
    rows () {
      if (this.typeFilter === 'all') {
        return this.withdrawNotesData
      }
      return this.withdrawNotesData.filter((item) => this.docType(item) === this.typeFilter)
    },
    activeCount () {
      return this.tabsTitle[this.tabs].count || 0
    },
    rangeStart () {
      return this.activeCount === 0 ? 0 : (this.page - 1) * this.perPage + 1
    },
    rangeEnd () {
      return Math.min(this.page * this.perPage, this.activeCount)
    },
    isEmpty () {
      return this.rows.length === 0
    },
    canSelect () {
      return this.noteStatus !== 'canceled'
    },
    selectedItems () {
      return this.withdrawNotesData.filter((item) => this.selected[item.id])
    },
    selectedCount () {
      return this.selectedItems.length
    },
    hasSelection () {
      return this.selectedCount > 0
    },
    canPrint () {
      return this.hasSelection && this.canSelect
    },
    allSelected () {
      const visible = this.rows
      return visible.length > 0 && visible.every((item) => this.selected[item.id])
    },
    isDisabled () {
      return this.warehouseSelected === 0 || this.warehouseSelected === null
    },
    isDisabledByRole () {
      const wnRole = [
        'management',
        'area_manager',
        'marketing',
        'developer',
        'creative',
        'merchandising_planner',
        'merchandiser',
        'online_admin',
        'accounting_manager',
        'warehouse_manager',
        'warehouse',
        'store_manager',
        'vm',
        'acting_assist_store_manager',
        'inventory_and_costing',
        'assist_store_manager',
        'sales_staff'
      ]
      const role = getRole()
      return !wnRole.some((r) => r === role)
    }
  },
  watch: {
    warehouseSelected () {
      this.resetAndFetch()
    },
    warehouses (val) {
      if (!this.warehouseSelected && val.length >= 1) {
        this.warehouseSelected = val[0].id
      }
    }
  },
  mounted () {
    if (!this.warehouseSelected && this.warehouses.length >= 1) {
      this.warehouseSelected = this.warehouses[0].id
    } else if (this.warehouseSelected !== null) {
      this.resetAndFetch()
    }
  },
  methods: {
    ...mapActions({
      setSnackbar: 'Components/setSnackbar',
      setErrorPage: 'Components/setErrorPage'
    }),
    formatNumber (n) {
      return Number(n || 0).toLocaleString('en-US')
    },
    formatDate (date) {
      return this.$dayjs(date).format('DD MMM YYYY')
    },
    formatTime (date) {
      return this.$dayjs(date).format('HH:mm:ss')
    },
    dueDateClass (date) {
      const diff = this.$dayjs(date).startOf('day').diff(this.$dayjs().startOf('day'), 'day')
      if (diff > 0) {
        return 'wn-due--future'
      }
      if (diff === 0) {
        return 'wn-due--today'
      }
      return 'wn-due--past'
    },
    docType (item) {
      return (item.code || '').slice(0, 3)
    },
    docNumber (item) {
      return (item.code || '').slice(3)
    },
    typeStyle (item) {
      const tc = TYPE_COLORS[this.docType(item)] || { fg: '#52606d' }
      return { color: tc.fg }
    },
    chipStyle (role) {
      const color = CHIP_COLORS[role] || CHIP_COLORS.origin
      return { background: color.bg, color: color.fg }
    },
    currentWarehouseName (item) {
      const found = this.warehouse.find((w) => +w.id === +(item.warehouse && item.warehouse.id))
      return found ? found.name : (item.warehouse && item.warehouse.name) || ''
    },
    destChip (item) {
      if (item.deliveryNoteRef && item.deliveryNoteRef.warehouseId) {
        const found = this.warehouse.find((w) => +w.id === +item.deliveryNoteRef.warehouseId)
        if (found) {
          return { id: found.id, name: found.name }
        }
      }
      return null
    },
    noteCompute (item) {
      let refs = ''
      if (item.withdrawNoteRef && item.withdrawNoteRef.code) {
        refs = `Withdraw Note : ${item.withdrawNoteRef.code}`
      } else if (item.deliveryNoteRef && item.deliveryNoteRef.code) {
        refs = `Delivery Note : ${item.deliveryNoteRef.code}`
      }

      let text = ''
      if (item.note && refs) {
        text = `${item.note} | from ${refs}`
      } else if (refs) {
        text = `from ${refs}`
      } else if (item.note) {
        text = item.note
      }

      return text
    },
    isSelected (item) {
      return !!this.selected[item.id]
    },
    toggleSelect (item) {
      this.$set(this.selected, item.id, !this.selected[item.id])
    },
    toggleAll () {
      const next = { ...this.selected }
      const target = !this.allSelected
      this.rows.forEach((item) => {
        if (target) {
          next[item.id] = true
        } else {
          delete next[item.id]
        }
      })
      this.selected = next
    },
    clearSelection () {
      this.selected = {}
    },
    changeTab (index) {
      if (this.tabs === index) {
        return
      }
      this.tabs = index
      this.resetAndFetch()
    },
    setWarehouse (id) {
      if (id !== undefined && id !== null) {
        this.warehouseSelected = id
      }
    },
    setPerPage (size) {
      if (this.perPage === size) {
        return
      }
      this.perPage = size
      this.resetAndFetch(false)
    },
    onSearchInput () {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.onSearch()
      }, 450)
    },
    onSearch () {
      clearTimeout(this.searchTimeout)
      this.resetAndFetch()
    },
    prevPage () {
      if (this.page > 1) {
        this.page--
        this.fetchWithdrawNote()
      }
    },
    nextPage () {
      if (this.page < this.totalPages) {
        this.page++
        this.fetchWithdrawNote()
      }
    },
    resetAndFetch (withCount = true) {
      if (this.warehouseSelected === null) {
        return
      }
      this.page = 1
      this.totalPages = 1
      this.selected = {}
      this.withdrawNotesData = []
      if (withCount) {
        this.fetchCount()
      }
      this.fetchWithdrawNote()
    },
    async fetchCount () {
      try {
        this.loadingCount = true
        const { data } = await InventoryService.getWithdrawNotesCount({
          search: this.searchInput,
          warehouse: this.warehouseSelected
        })
        for (const element of this.tabsTitle) {
          element.count = data[element.key]
        }
      } catch (error) {
        console.error('fetchCount', error)
      } finally {
        this.loadingCount = false
      }
    },
    async fetchWithdrawNote () {
      try {
        this.getting = true
        const { data } = await InventoryService.getWithdrawNotes({
          page: this.page,
          limit: this.perPage,
          search: this.searchInput,
          status: this.noteStatus,
          warehouse: this.warehouseSelected,
          sortBy: 'createdAt',
          sortOrder: this.noteStatus === 'pending' ? 'asc' : 'desc'
        })
        this.totalPages = data.pages || 1
        this.withdrawNotesData = data.results || []
      } catch (error) {
        console.error('fetchWithdrawNote', error)
        this.setErrorPage(error.code)
      } finally {
        this.getting = false
      }
    },
    async copyCode (item) {
      try {
        await navigator.clipboard.writeText(item.code || '')
        clearTimeout(this.copiedTimeout)
        this.copiedId = item.id
        this.copiedTimeout = setTimeout(() => {
          this.copiedId = null
        }, 1500)
      } catch (error) {
        console.error('copyCode', error)
        this.setSnackbar({
          value: true,
          message: 'Copy failed',
          type: 'error'
        })
      }
    },
    openDetail (item) {
      const routeData = this.$router.resolve({
        name: 'WithdrawNote',
        query: {
          id: item.id,
          warehouse: this.warehouseSelected
        }
      })
      window.open(routeData.href, '_blank')
    },
    printOne (item) {
      window.open(
        this.$router.resolve({
          name: 'MultiWithdrawNote',
          query: {
            id: [item.id],
            warehouse: [item.warehouse.id]
          }
        }).href,
        '_blank'
      )
    },
    printSelected () {
      if (!this.canPrint) {
        return
      }
      const id = []
      const warehouse = []
      this.selectedItems.forEach((item) => {
        id.push(item.id)
        warehouse.push(item.warehouse.id)
      })
      window.open(
        this.$router.resolve({
          name: 'MultiWithdrawNote',
          query: { id, warehouse }
        }).href,
        '_blank'
      )
    },
    async exportItems () {
      if (!this.hasSelection || this.exporting) {
        return
      }
      try {
        this.exporting = true
        const items = [...this.selectedItems]
        const details = await Promise.all(items.map((item) => InventoryService.getWithdrawNoteById(item.id)))

        const nameOf = (v) => (v && typeof v === 'object' ? (v.name || '') : (v || ''))

        const skuMap = {}
        details.forEach(({ data }, index) => {
          const docCode = (data && data.code) || items[index].code
          const products = (data && data.products) || []
          products.forEach((product) => {
            (product.skus || []).forEach((sku) => {
              const code = sku.code || ''
              if (!code) {
                return
              }
              if (!skuMap[code]) {
                skuMap[code] = {
                  code,
                  name: product.name || '',
                  color: nameOf(sku.color),
                  size: nameOf(sku.size),
                  count: 0,
                  amount: 0,
                  docs: new Set()
                }
              }
              skuMap[code].count += Number(sku.count || 0)
              skuMap[code].amount += Number(sku.amount || 0)
              skuMap[code].docs.add(docCode)
            })
          })
        })

        const rows = Object.values(skuMap)
        if (!rows.length) {
          this.setSnackbar({
            value: true,
            message: '\u0e44\u0e21\u0e48\u0e1e\u0e1a SKU \u0e43\u0e19\u0e43\u0e1a\u0e17\u0e35\u0e48\u0e40\u0e25\u0e37\u0e2d\u0e01',
            type: 'error'
          })
          return
        }

        const esc = (v) => String(v || '').replace(/"/g, '""')
        const header = ['Product Name', 'SKU Code', 'Color', 'Size', 'Amount', 'Count', 'Document No.']
        const lines = rows.map((row) => {
          const docs = [...row.docs].join(',')
          return [
            `"${esc(row.name)}"`,
            `"${esc(row.code)}"`,
            `"${esc(row.color)}"`,
            `"${esc(row.size)}"`,
            row.amount,
            row.count,
            `"${esc(docs)}"`
          ].join(',')
        })
        const csv = [header.join(','), ...lines].join('\n')
        const blob = new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `withdraw-note-items-${this.$dayjs().format('YYYYMMDD-HHmmss')}.csv`
        link.click()
        URL.revokeObjectURL(url)
      } catch (error) {
        console.error('exportItems', error)
        this.setSnackbar({
          value: true,
          message: `Error: ${error.message}`,
          type: 'error'
        })
      } finally {
        this.exporting = false
      }
    },
    createNote () {
      const routeData = this.$router.resolve({
        name: 'WithdrawNote',
        query: { warehouse: this.warehouseSelected }
      })
      window.open(routeData.href, '_blank')
    },
    goToOldVersion () {
      this.$router.push({ name: 'WithdrawNoteList' })
    }
  }
}
</script>

<style lang="scss" scoped>
@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&display=swap');

$primary: #0f5d7c;
$ink: #1f2933;
$muted: #7b8794;
$faint: #9aa5b1;

.wn-new {
  font-family: 'IBM Plex Sans', system-ui, sans-serif;
  color: $ink;
  -webkit-font-smoothing: antialiased;
  width: 100%;
  padding: 6px 4px 40px;

  * {
    box-sizing: border-box;
  }
}

.wn-switch-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
  padding: 10px 16px;
  border: 1px solid #f3a9d4;
  background: #faf0f6;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: #f7e6f1;
  }

  &__text {
    font-size: 14px;
    font-weight: 600;
    color: #16160f;
  }

  &__cta {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    color: #16160f;
  }
}

.wn-page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 24px;
}

.wn-title {
  margin: 0;
  font-size: 27px;
  font-weight: 700;
  letter-spacing: -0.4px;
  color: #1a2b34;
}

.wn-subtitle {
  margin: 7px 0 0;
  font-size: 14px;
  color: $muted;
}

.wn-header-actions {
  display: flex;
  gap: 12px;
  flex-shrink: 0;
}

.wn-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 44px;
  padding: 0 20px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.15s, opacity 0.15s;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  &--primary {
    border: 1.5px solid $primary;
    background: $primary;
    color: #fff;
    padding: 0 22px;
    box-shadow: 0 2px 6px rgba(15, 93, 124, 0.25);

    &:hover:not(:disabled) {
      background: #0c4d68;
    }
  }
}

.wn-card {
  background: #fff;
  border: 1px solid #e4e8ec;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(16, 42, 67, 0.04), 0 8px 24px rgba(16, 42, 67, 0.04);
  overflow: hidden;
}

.wn-toolbar {
  display: flex;
  align-items: flex-end;
  gap: 16px;
  flex-wrap: wrap;
  padding: 20px 24px;
  border-bottom: 1px solid #eef1f4;
}

.wn-field {
  display: flex;
  flex-direction: column;
  gap: 6px;

  &__label {
    font-size: 11.5px;
    font-weight: 600;
    color: $faint;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
}

.wn-autocomplete {
  min-width: 230px;
  font-family: inherit;

  ::v-deep .v-input__slot {
    min-height: 42px !important;
    padding: 0 14px !important;
    border: 1.5px solid #dce1e6 !important;
    background: #fff !important;
    border-radius: 10px !important;
    box-shadow: none !important;
    transition: border-color 0.15s;
  }

  ::v-deep .v-input__slot:hover {
    border-color: #b6c0c9 !important;
  }

  &.v-input--is-focused ::v-deep .v-input__slot {
    border-color: $primary !important;
    box-shadow: 0 0 0 3px rgba(15, 93, 124, 0.12) !important;
  }

  ::v-deep input {
    font-size: 14px;
    font-weight: 600;
    color: $ink;

    &::placeholder {
      font-weight: 500;
      color: $faint;
    }
  }

  ::v-deep .v-select__selection {
    font-size: 14px;
    font-weight: 600;
    color: $ink;
  }

  ::v-deep .v-input__append-inner {
    margin-top: 0 !important;
    align-self: center;

    .v-icon {
      font-size: 20px;
      color: $muted;
    }
  }
}

.wn-spacer {
  flex: 1;
}

.wn-search {
  position: relative;
  display: flex;
  align-items: center;

  &__icon {
    position: absolute;
    left: 14px;
    pointer-events: none;
  }

  &__input {
    width: 320px;
    max-width: 100%;
    height: 42px;
    padding: 0 14px 0 40px;
    border: 1.5px solid #dce1e6;
    background: #fff;
    border-radius: 10px;
    font-size: 14px;
    font-family: inherit;
    color: $ink;
    outline: none;

    &:focus {
      border-color: $primary;
      box-shadow: 0 0 0 3px rgba(15, 93, 124, 0.12);
    }
  }
}

.wn-tabs-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  border-bottom: 1px solid #eef1f4;
  flex-wrap: wrap;
  gap: 8px;
}

.wn-tabs {
  display: flex;
  gap: 4px;
}

.wn-tab {
  display: flex;
  align-items: center;
  gap: 9px;
  height: 52px;
  padding: 0 8px;
  margin: 0 8px;
  border: none;
  background: transparent;
  font-family: inherit;
  font-size: 14px;
  cursor: pointer;
  font-weight: 600;
  color: $muted;
  border-bottom: 2.5px solid transparent;

  &--active {
    font-weight: 700;
    color: $primary;
    border-bottom-color: $primary;

    .wn-tab__badge {
      background: #e1f0f7;
      color: #0d6a8f;
    }
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 26px;
    padding: 1px 9px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 700;
    background: #f0f3f6;
    color: $faint;
  }
}

.wn-tabs-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.wn-action-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 38px;
  padding: 0 16px;
  border-radius: 9px;
  font-size: 13.5px;
  font-weight: 600;
  font-family: inherit;
  border: 1px solid #cfe0e8;
  background: #fff;
  color: $primary;
  cursor: pointer;

  &:disabled {
    border-color: #e4e8ec;
    background: #f4f6f8;
    color: #b6c0c9;
    cursor: not-allowed;
  }

  &--primary {
    border-color: $primary;
    background: $primary;
    color: #fff;

    &:disabled {
      border-color: #e4e8ec;
      background: #f4f6f8;
      color: #b6c0c9;
    }
  }
}

.wn-sel-banner {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 24px;
  background: #eef7fb;
  border-bottom: 1px solid #d6e9f2;

  &__text {
    font-size: 13.5px;
    font-weight: 600;
    color: $primary;
  }
}

.wn-sel-btn {
  height: 34px;
  padding: 0 14px;
  border: 1px solid #cdd9e0;
  background: #fff;
  color: #52606d;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;

  &--danger {
    border-color: #e7b9b9;
    color: #d23b3b;

    &:hover {
      background: #fdf2f2;
    }
  }

  &--solid {
    border: none;
    background: $primary;
    color: #fff;
    padding: 0 16px;
  }
}

.wn-pagination {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 34px;
  padding: 12px 28px;
  background: #fafbfc;
  flex-wrap: wrap;

  &--top {
    border-bottom: 1px solid #eef1f4;
  }

  &--footer {
    border-top: 1px solid #eef1f4;
    padding: 14px 28px;
  }
}

.wn-rows-per-page {
  display: flex;
  align-items: center;
  gap: 12px;

  &__label {
    font-size: 13px;
    color: $muted;
  }

  &__btn {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 32px;
    padding: 0 6px 0 8px;
    border: none;
    border-bottom: 1px solid #cbd2d9;
    background: transparent;
    font-size: 13.5px;
    font-weight: 600;
    color: #3e4c59;
    font-family: inherit;
    cursor: pointer;

    &:hover {
      border-bottom-color: $primary;
    }
  }
}

.wn-range {
  font-size: 13.5px;
  color: #52606d;
  font-variant-numeric: tabular-nums;
}

.wn-pager {
  display: flex;
  align-items: center;
  gap: 4px;

  &__btn {
    width: 34px;
    height: 34px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: transparent;
    color: #52606d;
    border-radius: 8px;
    cursor: pointer;

    &:hover:not(:disabled) {
      background: #eef1f4;
    }

    &:disabled {
      color: #b6c0c9;
      cursor: not-allowed;
    }
  }
}

.wn-table-scroll {
  overflow-x: auto;
}

.wn-table {
  min-width: 1120px;
}

.wn-row {
  display: grid;
  grid-template-columns: 46px 300px 148px 148px minmax(260px, 1fr) 116px;
  align-items: center;
  column-gap: 16px;
  padding: 15px 24px;
  border-bottom: 1px solid #f0f3f5;
  background: #fff;
  transition: background 0.12s;

  &:hover:not(&--head) {
    background: #f7fafb;
  }

  &--head {
    padding: 13px 24px;
    background: #fafbfc;
    border-bottom: 1px solid #eef1f4;
  }

  &--selected {
    background: #eef7fb;

    &:hover {
      background: #eef7fb;
    }
  }
}

.wn-th {
  font-size: 11.5px;
  font-weight: 700;
  color: $muted;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &--right {
    text-align: right;
  }

  &--center {
    text-align: center;
  }
}

.wn-cell--center {
  text-align: center;
}

.wn-cell-check {
  display: flex;
  align-items: center;
}

.wn-check {
  width: 19px;
  height: 19px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: 1.5px solid #cbd2d9;
  background: #fff;
  transition: all 0.12s;

  &--on {
    border-color: $primary;
    background: $primary;
  }
}

.wn-doc {
  display: flex;
  align-items: center;
  font-family: 'IBM Plex Mono', monospace;
  font-size: 13.5px;
  font-weight: 600;
  letter-spacing: -0.2px;

  &__type {
    display: inline-flex;
    align-items: center;
    margin-right: 7px;
    font-family: 'IBM Plex Sans', sans-serif;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.4px;
  }

  &__code {
    color: $primary;
    text-decoration: none;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  &__copy {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    margin-left: 6px;
    padding: 0;
    border: none;
    background: transparent;
    color: $faint;
    border-radius: 6px;
    cursor: pointer;
    transition: background 0.12s, color 0.12s;

    &:hover {
      background: #eef1f4;
      color: $primary;
    }
  }
}

.wn-loc {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  margin-top: 9px;

  &__chip {
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
    white-space: nowrap;
    height: 21px;
    padding: 0 8px;
    border-radius: 6px;
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.3px;
  }

  &__arrow {
    flex-shrink: 0;
  }
}

.wn-date {
  font-size: 13px;
  font-weight: 500;
  color: #3e4c59;

  &--empty {
    color: $faint;
  }
}

.wn-due {
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 10px;
  border-radius: 7px;
  font-weight: 600;

  &--future {
    background: #e4f6ea;
    color: #1f8a50;
  }

  &--today {
    background: #fdf3d7;
    color: #b7791f;
  }

  &--past {
    background: #fdecec;
    color: #d23b3b;
  }
}

.wn-time {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 12px;
  color: $faint;
  margin-top: 2px;
}

.wn-note {
  font-size: 13px;
  color: #52606d;
  line-height: 1.45;
  min-width: 0;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.wn-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

.wn-icon-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #dce1e6;
  background: #fff;
  color: $muted;
  border-radius: 8px;
  cursor: pointer;

  &:hover {
    background: #f7f9fa;
    color: #52606d;
  }
}

.wn-icon-btn--printed {
  color: #2e7d32;
}

.wn-empty {
  padding: 64px 24px;
  text-align: center;
  color: $faint;

  &__title {
    font-size: 15px;
    font-weight: 600;
    color: $muted;
  }

  &__sub {
    font-size: 13px;
    margin-top: 6px;
  }
}
</style>
